import FormCard from "../../../../../Lib/FormCard";
import FormSelect from "../../../../../Lib/FormSelect";

import { useState, useEffect } from "react";

const IdentifyForm = ({
  formData,
  handleChange,
  handleFileChange,
  isViewOnly = false,
  guestDocumentUrl, // Added prop for view mode to show existing documents
}) => {
  const [filePreview, setFilePreview] = useState(null);
  const [fileType, setFileType] = useState(null);
  const [hasNewFile, setHasNewFile] = useState(false);

  // Initialize with existing document URL if in view mode
  useEffect(() => {
    if (isViewOnly && guestDocumentUrl) {
      setFilePreview(guestDocumentUrl);
      // Determine file type from URL extension if not available in formData
      if (guestDocumentUrl.toLowerCase().endsWith(".pdf")) {
        setFileType("pdf");
      } else {
        setFileType("image");
      }
      return;
    }

    // If a new file is selected, prioritize it over existing URL
    if (formData.guestDocuments) {
      const url = URL.createObjectURL(formData.guestDocuments);
      setFilePreview(url);
      setHasNewFile(true);

      if (formData.guestDocuments.type.startsWith("image")) {
        setFileType("image");
      } else if (formData.guestDocuments.type === "application/pdf") {
        setFileType("pdf");
      }
    } else if (guestDocumentUrl && !hasNewFile) {
      // Show existing document if no new file is selected
      setFilePreview(guestDocumentUrl);
      if (guestDocumentUrl.toLowerCase().endsWith(".pdf")) {
        setFileType("pdf");
      } else {
        setFileType("image");
      }
    } else if (!formData.guestDocuments && !guestDocumentUrl) {
      setFilePreview(null);
      setFileType(null);
      setHasNewFile(false);
    }
  }, [formData.guestDocuments, guestDocumentUrl, isViewOnly, hasNewFile]);

  const handleFileSelect = (file) => {
    setHasNewFile(true);
    handleFileChange(file);
  };

  const documentOptions = [
    { value: "passport", label: "Passport" },
    { value: "driving_license", label: "Driving License" },
    { value: "aadhar", label: "Aadhar" },
  ];

  return (
    <FormCard title="Identity Details">
      <div className="row g-3">
        <div className="col-md-6">
          {isViewOnly ? (
            <div className="view-only-field">
              <label className="form-label">
                <i className="bi bi-card-text me-2"></i>
                Document Type
              </label>
              <div className="view-only-value">
                {/* {documentOptions.find(
                  (opt) => opt.value === formData.document_type
                )?.label || "N/A"} */}
              {formData.document_type || ""}
              </div>
            </div>
          ) : (
            <FormSelect
              label="Document Type"
              icon="bi bi-card-text"
              name="document_type"
              value={formData.document_type || ""}
              onChange={handleChange}
              options={documentOptions}
            />
          )}
        </div>

        <div className="col-md-12">
          {isViewOnly ? (
            <div className="view-only-field">
              <label className="form-label">Document</label>
              {filePreview ? (
                <div className="mt-2">
                  {fileType === "image" ? (
                    <img
                      src={filePreview || "/placeholder.svg"}
                      alt="Document Preview"
                      className="img-fluid rounded border"
                      style={{ maxWidth: "300px", maxHeight: "200px" }}
                    />
                  ) : (
                    <div className="border p-2">
                      <i className="bi bi-file-earmark-pdf fs-1 text-danger"></i>
                      <div>PDF Document</div>
                      <a
                        href={filePreview}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm btn-outline-primary mt-2"
                      >
                        View Document
                      </a>
                    </div>
                  )}
                </div>
              ) : (
                <div className="view-only-value">No document uploaded</div>
              )}
            </div>
          ) : (
            <>
              <label className="form-label">Document Upload</label>
              <input
                type="file"
                className="form-control"
                name="guestDocuments"
                accept="image/*,application/pdf"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    handleFileSelect(e.target.files[0]);
                  }
                }}
                disabled={isViewOnly}
              />

              {/* Show existing document if available and no new file selected */}
              {guestDocumentUrl && !hasNewFile && (
                <div className="mt-3">
                  <div className="text-muted small mb-2">Current Document:</div>
                  {guestDocumentUrl.toLowerCase().endsWith(".pdf") ? (
                    <div className="border p-2 rounded">
                      <i className="bi bi-file-earmark-pdf fs-1 text-danger"></i>
                      <div>Current PDF Document</div>
                      <a
                        href={guestDocumentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm btn-outline-primary mt-2"
                      >
                        View Current Document
                      </a>
                    </div>
                  ) : (
                    <img
                      src={guestDocumentUrl || "/placeholder.svg"}
                      alt="Current Document"
                      className="img-fluid rounded border"
                      style={{ maxWidth: "300px", maxHeight: "200px" }}
                    />
                  )}
                </div>
              )}

              {/* Show preview of newly selected file */}
              {filePreview && hasNewFile && (
                <div className="mt-3">
                  <div className="text-success small mb-2">
                    New Document Preview:
                  </div>
                  {fileType === "image" ? (
                    <img
                      src={filePreview || "/placeholder.svg"}
                      alt="New Document Preview"
                      className="img-fluid rounded border"
                      style={{ maxWidth: "300px", maxHeight: "200px" }}
                    />
                  ) : (
                    <div className="border p-2 rounded">
                      <i className="bi bi-file-earmark-pdf fs-1 text-success"></i>
                      <div>New PDF Preview</div>
                      <a
                        href={filePreview}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm btn-outline-success mt-2"
                      >
                        Open New Document
                      </a>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div className="col-md-12">
          {isViewOnly ? (
            <div className="view-only-field">
              <label className="form-label">Comments</label>
              <div className="view-only-value">
                {formData.comment || "No comments"}
              </div>
            </div>
          ) : (
            <div className="form-group">
              <label className="form-label">Comments</label>
              <textarea
                className="form-control"
                placeholder="Additional comments"
                name="comment"
                value={formData.comment || ""}
                onChange={handleChange}
                disabled={isViewOnly}
              />
            </div>
          )}
        </div>
      </div>
    </FormCard>
  );
};

export default IdentifyForm;
